import React, { useEffect, useMemo, useRef, useState } from "react";
import IncidentMap from "@/components/cad/IncidentMap";
import IncidentDetailDialog from "@/components/cad/IncidentDetailDialog";
import IncidentInfoDialog from "@/components/cad/IncidentInfoDialog";
import { useIncidents } from "@/contexts/IncidentsContext";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Crosshair, Pause, Play, Pencil, Trash2 } from "lucide-react";
import useIncidentActivity from "@/hooks/useIncidentActivity";
import { toast } from "sonner";
import { api } from "@/lib/api";

import { isIncidentVisible, lastActivityMs } from "@/lib/incidentVisibility";

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }); } catch { return iso; }
}

export default function ActiveIncidentsPage() {
  const { incidents, lastUpdated } = useIncidents();
  const { user } = useAuth();
  const canDelete = ["supervisor", "admin_dispatcher", "admin"].includes(user?.role);

  const handleDelete = async (inc, e) => {
    e?.stopPropagation?.();
    if (!window.confirm(`Delete ${inc.incident_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/incidents/${inc.id}`);
      toast.success(`${inc.incident_number} deleted`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const openUpdate = (inc, e) => {
    e?.stopPropagation?.();
    setSelected(inc);
    setDetailOpen(true);
  };
  const [center, setCenter] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  // Read-only "incident detail" popup opened from the map's Show More button.
  // Kept separate from `detailOpen` (the update form dialog) so Show More
  // never accidentally lands the dispatcher on the update screen.
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoIncident, setInfoIncident] = useState(null);
  const [autoFocus, setAutoFocus] = useState(true);
  const lastSeenRef = useRef(null);
  // Track whether we've done the initial "land on the last dispatched call"
  // centering. On this page the map is meant to STAY on the last call — we
  // never fall back to browser geolocation like the CAD screen does.
  const initialCenteredRef = useRef(false);

  useEffect(() => {
    if (!lastUpdated || !autoFocus) return;
    if (lastSeenRef.current === lastUpdated.id + ":" + lastUpdated.updated_at) return;
    lastSeenRef.current = lastUpdated.id + ":" + lastUpdated.updated_at;
    if (lastUpdated.lat && lastUpdated.lon) {
      setCenter([lastUpdated.lat, lastUpdated.lon]);
      setSelected(lastUpdated);
    }
  }, [lastUpdated, autoFocus]);

  // Show every incident whose LAST activity (creation, update, page, or update-log
  // entry) is within the last 12 hours. Once 12 h passes with no activity, the
  // incident falls off both this board and the map.
  const recent = useMemo(() => {
    return incidents
      .filter((i) => isIncidentVisible(i))
      .sort((a, b) => lastActivityMs(b) - lastActivityMs(a));
  }, [incidents]);

  // Active incidents (for map): non-closed/cleared from the recent window
  const active = useMemo(
    () => recent.filter((i) => !["closed", "cleared", "cancelled"].includes((i.status || "").toLowerCase())),
    [recent]
  );

  const flashing = useIncidentActivity(recent, { durationMs: 8000 });

  // Once we have any active incidents, land the map on the newest one. Runs
  // exactly once per page mount — after that, the auto-focus effect above
  // keeps the map tracking new pages, and the map otherwise sits still.
  useEffect(() => {
    if (initialCenteredRef.current) return;
    const newest = active[0]; // sorted by lastActivityMs desc
    if (!newest) return;
    if (newest.lat && newest.lon) {
      setCenter([newest.lat, newest.lon]);
      setSelected(newest);
      lastSeenRef.current = newest.id + ":" + newest.updated_at;
      initialCenteredRef.current = true;
    }
  }, [active]);

  // "Re-Center" on this page snaps back to whichever call is currently the
  // most recent active dispatch — NOT the dispatcher's browser location.
  const recenterOnLastCall = () => {
    const target = lastUpdated && lastUpdated.lat && lastUpdated.lon ? lastUpdated : active[0];
    if (target && target.lat && target.lon) {
      setCenter([target.lat, target.lon]);
      setSelected(target);
    }
  };

  const openIncident = (inc, e) => {
    e?.stopPropagation?.();
    setSelected(inc);
    if (inc.lat && inc.lon) setCenter([inc.lat, inc.lon]);
  };

  // ── Resizable bottom sheet (mobile only) ─────────────────────────────────
  // On phones the active-incident panel overlays the map so dispatchers keep
  // seeing pins while scanning the list. The user can drag the top handle up
  // or down; height is stored as a viewport-height percentage so it survives
  // rotations / URL bar shuffles. The `--panel-h` CSS variable is consumed by
  // the panel's `h-[var(--panel-h)]` class below. Desktop overrides it with
  // `md:h-72` via a higher-specificity breakpoint rule.
  const MIN_VH = 12;   // just the header + one row
  const MID_VH = 45;   // half the map
  const MAX_VH = 85;   // almost full-screen list
  const [panelVh, setPanelVh] = useState(30);
  const dragStartRef = useRef(null); // { startY, startVh }
  const onHandlePointerDown = (e) => {
    // Only enable drag on touch-first / small-screen viewports.
    if (typeof window !== "undefined" && window.innerWidth >= 768) return;
    dragStartRef.current = { startY: e.clientY, startVh: panelVh };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    e.preventDefault();
  };
  const onHandlePointerMove = (e) => {
    if (!dragStartRef.current) return;
    const dy = dragStartRef.current.startY - e.clientY;      // up = positive
    const dvh = (dy / window.innerHeight) * 100;
    const next = Math.max(MIN_VH, Math.min(MAX_VH, dragStartRef.current.startVh + dvh));
    setPanelVh(next);
  };
  const onHandlePointerUp = (e) => {
    if (!dragStartRef.current) return;
    // Snap to the nearest anchor so the panel doesn't rest at an awkward height.
    const anchors = [MIN_VH, MID_VH, MAX_VH];
    const snapped = anchors.reduce((best, a) => Math.abs(a - panelVh) < Math.abs(best - panelVh) ? a : best, anchors[0]);
    setPanelVh(snapped);
    dragStartRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const cyclePanelHeight = () => {
    // Tap-to-cycle for accessibility (users who can't easily drag). Progresses
    // MIN → MID → MAX → MIN.
    if (panelVh < (MIN_VH + MID_VH) / 2) setPanelVh(MID_VH);
    else if (panelVh < (MID_VH + MAX_VH) / 2) setPanelVh(MAX_VH);
    else setPanelVh(MIN_VH);
  };

  return (
    <div
      className="absolute inset-0 flex flex-col"
      data-testid="active-incidents-page"
      style={{ "--panel-h": `${panelVh}vh` }}
    >
      {/* Emergency banner removed — audio + map auto-focus + list flash convey new pages */}

      <div className="flex-1 flex flex-col relative min-h-0">
        {/* Map area — flex child so panel sibling below stacks correctly on md+ */}
        <div className="flex-1 relative min-h-0">
          <IncidentMap
            incidents={active}
            center={center}
            onSelect={openIncident}
            onOpenDetail={(inc) => { setInfoIncident(inc); setInfoOpen(true); }}
            onOpenUpdate={(inc) => { setSelected(inc); setDetailOpen(true); }}
            selectedId={selected?.id}
            flyZoom={15}
            height="100%"
          />

          <div className="absolute top-3 left-3 right-3 z-[400] flex gap-2 flex-wrap">
            <Button
              size="sm" variant="outline"
              onClick={recenterOnLastCall}
              data-testid="active-recenter-btn"
              className="shadow-lg"
              title="Snap back to the last dispatched call"
            ><Crosshair className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Re-Center on Last Call</span><span className="sm:hidden">Re-Center</span></Button>
            <Button
              size="sm" variant={autoFocus ? "default" : "outline"}
              onClick={() => setAutoFocus((v) => !v)}
              data-testid="active-auto-focus-toggle"
              className="shadow-lg font-mono tracking-widest"
            >
              {autoFocus ? <Pause className="w-3.5 h-3.5 mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
              <span className="hidden sm:inline">{autoFocus ? "AUTO-FOCUS: ON" : "AUTO-FOCUS: OFF"}</span>
              <span className="sm:hidden">{autoFocus ? "AF ON" : "AF OFF"}</span>
            </Button>
          </div>
        </div>

        {/* Active Incident Panel:
              • Mobile (< md): absolute bottom sheet overlaying the map — drag
                handle lets the dispatcher size it between 12–85 vh.
              • md+: static sibling below the map with a fixed 18rem height. */}
        <div
          className="absolute inset-x-0 bottom-0 z-[500] h-[var(--panel-h)] md:relative md:z-auto md:h-72 md:shrink-0 md:inset-auto border-t border-border bg-card/95 backdrop-blur flex flex-col shadow-2xl md:shadow-none"
          data-testid="active-incident-panel"
        >
          {/* Drag handle — mobile-only visible / interactive; also a tap-to-cycle target */}
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize incident list"
            onPointerDown={onHandlePointerDown}
            onPointerMove={onHandlePointerMove}
            onPointerUp={onHandlePointerUp}
            onPointerCancel={onHandlePointerUp}
            onDoubleClick={cyclePanelHeight}
            className="md:hidden flex items-center justify-center h-6 shrink-0 cursor-ns-resize touch-none select-none"
            data-testid="active-panel-drag-handle"
          >
            <div className="w-12 h-1.5 rounded-full bg-muted-foreground/40" />
          </div>
          <div className="h-10 px-4 flex items-center justify-between border-b border-border">
            <div className="flex items-center gap-3">
              <div className="font-mono text-xs tracking-widest text-primary">// ACTIVE INCIDENT PANEL · LAST 12H</div>
              <Badge variant="outline" data-testid="active-incidents-count">{recent.length} job{recent.length === 1 ? "" : "s"}</Badge>
            </div>
            <button
              type="button"
              onClick={cyclePanelHeight}
              className="md:hidden text-[10px] font-mono text-muted-foreground px-2 py-1 border border-border rounded-sm"
              data-testid="active-panel-cycle-btn"
              title="Cycle panel size"
            >
              {panelVh <= MIN_VH + 5 ? "PEEK" : panelVh <= MID_VH + 5 ? "HALF" : "FULL"}
            </button>
            <div className="hidden md:block font-mono text-[10px] text-muted-foreground">Click row to zoom & view detail · yellow flash = new/updated</div>
          </div>

          <ScrollArea className="flex-1">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card/95 backdrop-blur z-10">
              <tr className="text-left font-mono text-[10px] text-muted-foreground tracking-widest border-b border-border">
                <th className="p-2">INCIDENT #</th>
                <th className="p-2">CALL TYPE</th>
                <th className="p-2">ADDRESS</th>
                <th className="p-2">TIME</th>
                <th className="p-2">LAST PAGED</th>
                <th className="p-2 text-right">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {recent.map((i) => {
                const isFlash = flashing.has(i.id);
                return (
                  <tr
                    key={i.id}
                    data-testid={`incident-row-${i.incident_number}`}
                    className={`border-b border-border/50 hover:bg-accent/40 cursor-pointer ${selected?.id === i.id ? "bg-accent" : ""} ${isFlash ? "activity-flash" : ""}`}
                    onClick={(e) => openIncident(i, e)}
                  >
                    <td className="p-2 text-primary">{i.incident_number}</td>
                    <td className="p-2 font-sans">{i.call_type}</td>
                    <td className="p-2 font-sans truncate max-w-[360px]">{i.address}, {i.city} {i.state}</td>
                    <td className="p-2 text-muted-foreground">{fmtTime(i.created_at)}</td>
                    <td className="p-2 text-muted-foreground text-xs">{fmtTime(i.last_paged_at)} · {i.page_count || 1}×</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={(e) => openUpdate(i, e)}
                        data-testid={`row-update-${i.id}`}
                        title="Update / re-page this incident"
                      >
                        <Pencil className="w-3.5 h-3.5 text-primary" />
                      </Button>
                      {canDelete && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={(e) => handleDelete(i, e)}
                          data-testid={`row-delete-${i.id}`}
                          title="Delete this incident (supervisor/admin only)"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!recent.length && (
                <tr><td colSpan={6} className="p-12 text-center text-muted-foreground font-mono text-xs">NO INCIDENTS IN LAST 12 HOURS</td></tr>
              )}
            </tbody>
          </table>
        </ScrollArea>
        </div>
      </div>

      <IncidentDetailDialog incident={selected} open={detailOpen} onOpenChange={setDetailOpen} />
      <IncidentInfoDialog
        incident={infoIncident}
        open={infoOpen}
        onOpenChange={setInfoOpen}
        onUpdate={(inc) => { setSelected(inc); setDetailOpen(true); }}
      />
    </div>
  );
}
